import internalApi from './internalApi'

/* Référentiel des types de lieu — servi par admin-service (lot 4).
   Remplace trois listes codées en dur qui ne se parlaient pas : la List.of de
   ReferenceController, la _taxonomy du binaire Flutter et le
   FALLBACK_POI_CATEGORIES de ce même React. */

export interface TypeLieuCategorie {
  id: number
  libelle: string
  ordre: number
  /** Proposable au ciblage client F1. « Autre » est comptable mais pas ciblable. */
  ciblable: boolean
}

export interface TypeLieuSousType {
  id: number
  libelle: string
  actif: boolean
  categorie: TypeLieuCategorie
}

export async function getTypeLieuCategories(ciblable?: boolean): Promise<TypeLieuCategorie[]> {
  const res = await internalApi.get('/type-lieu/categories', {
    params: ciblable === undefined ? {} : { ciblable },
  })
  return Array.isArray(res.data) ? res.data : []
}

export async function getSousTypes(categorie?: number, actif?: boolean): Promise<TypeLieuSousType[]> {
  const res = await internalApi.get('/type-lieu/sous-types', {
    params: {
      ...(categorie === undefined ? {} : { categorie }),
      ...(actif === undefined ? {} : { actif }),
    },
  })
  return Array.isArray(res.data) ? res.data : []
}

export interface TypeLieuComplet {
  id: number
  libelle: string
  ordre: number
  ciblable: boolean
  sousTypes: string[]
  /** Identifiants stables des sous-types (Z2-22) ; absent d'un admin-service antérieur. */
  sousTypesDetail?: { id: number; libelle: string }[]
}

/* Identifiant d'un sous-type d'après son libellé, s'il est unique au
   référentiel ; sinon undefined, et le serveur joint par libellé. */
export function idSousTypeUnique(taxonomie: TypeLieuComplet[], libelle: string): number | undefined {
  const ids = taxonomie.flatMap(c => (c.sousTypesDetail ?? []).filter(s => s.libelle === libelle).map(s => s.id))
  return ids.length === 1 ? ids[0] : undefined
}

/* Les deux niveaux en un appel. Destiné à l'app, utilisé aussi par la page de
   tarification des Tips, qui a besoin des sous-types proposables. */
export async function getTaxonomieComplete(ciblable?: boolean): Promise<TypeLieuComplet[]> {
  const res = await internalApi.get('/type-lieu/complet', {
    params: ciblable === undefined ? {} : { ciblable },
  })
  return Array.isArray(res.data) ? res.data : []
}

export const creerTypeLieuCategorie = (libelle: string, ciblable: boolean) =>
  internalApi.post('/type-lieu/categories', { libelle, ciblable })

export const modifierTypeLieuCategorie = (
  id: number, corps: { libelle?: string; ordre?: number; ciblable?: boolean },
) => internalApi.put(`/type-lieu/categories/${id}`, corps)

/* Échange avec la voisine et renumérote côté serveur, en une transaction :
   deux PUT successifs pouvaient laisser deux catégories au même ordre (Z2-39). */
export const deplacerTypeLieuCategorie = (id: number, sens: 'haut' | 'bas') =>
  internalApi.post(`/type-lieu/categories/${id}/deplacer`, { sens })

export const supprimerTypeLieuCategorie = (id: number) =>
  internalApi.delete(`/type-lieu/categories/${id}`)

export const creerSousType = (libelle: string, categorieId: number) =>
  internalApi.post('/type-lieu/sous-types', { libelle, categorieId })

export const modifierSousType = (
  id: number, corps: { libelle?: string; categorieId?: number },
) => internalApi.put(`/type-lieu/sous-types/${id}`, corps)

export const basculerSousTypeActif = (id: number, actif: boolean) =>
  internalApi.patch(`/type-lieu/sous-types/${id}/actif`, { actif })

/* Le serveur renvoie ses refus d'unicité en 409 avec un message affichable :
   on le rend tel quel plutôt que d'en fabriquer un approximatif. Même lecture
   que produitService, le format d'erreur d'admin-service étant le même. */
export function messageErreur(err: any, defaut: string): string {
  const d = err?.response?.data
  if (typeof d === 'string' && d.trim()) return d.trim()
  if (d && typeof d.message === 'string' && d.message.trim()) return d.message.trim()
  return defaut
}

/* Combien de POI portent ce sous-type, et combien de collecteurs distincts.
   Sert à voir, AVANT de lancer un bonus, s'il est voué à l'échec. */
export async function compterPoiParSousType(
  sousType: string, sousTypeId?: number,
): Promise<{ total: number; collecteurs: number }> {
  try {
    const locationApi = (await import('./locationApi')).default
    const res = await locationApi.post('/location/collecteurs-par-detail', {
      categoryDetail: sousType,
      ...(sousTypeId === undefined ? {} : { categoryDetailId: sousTypeId }),
    })
    return {
      total: res.data?.total ?? 0,
      collecteurs: Array.isArray(res.data?.collecteurs) ? res.data.collecteurs.length : 0,
    }
  } catch {
    return { total: 0, collecteurs: 0 }
  }
}
